import {
    Cartesian3,
    Cartographic,
    sampleTerrainMostDetailed,
    type Camera,
    type EasingFunction,
    type HeadingPitchRange,
    type Matrix4,
    type Rectangle,
    type Viewer,
} from 'cesium';

type FlyToOptions = {
  destination?: Cartesian3 | Rectangle;
  orientation?: HeadingPitchRange;
  duration?: number;
  complete?: Camera.FlightCompleteCallback;
  cancel?: Camera.FlightCancelledCallback;
  endTransform?: Matrix4;
  maximumHeight?: number;
  pitchAdjustHeight?: number;
  flyOverLongitude?: number;
  flyOverLongitudeWeight?: number;
  convert?: boolean;
  easingFunction?: EasingFunction.Callback;
};

/**
 * Flies the Cesium viewer's camera to a given centroid coordinate,
 * adjusting altitude based on terrain data when available.
 *
 * @async
 * @function flyToCentroid
 * @param {Viewer} viewer - The Cesium Viewer instance.
 * @param {string} centroid - A space-separated longitude/latitude pair
 * @param {number} [heightOffset=1000] - Additional height (in meters)
 *   to apply above the terrain or ground level.
 * @param {FlyToOptions} [flyToOptions={}] - Optional fly-to options.
 *
 * @returns {Promise<void>} Resolves when the camera fly-to animation has started.
 *
 * @throws {Error} If no terrain provider is available when attempting to
 *   sample terrain data.
 */

export async function flyToCentroid(
  viewer: Viewer,
  centroid: string,
  heightOffset: number = 1000,
  flyToOptions: FlyToOptions = {},
): Promise<void> {
  if (!centroid || !viewer) return;

  const [longitude, latitude] = centroid.split(' ').map(Number);
  let height = 0;

  const terrainProvider = viewer.terrainProvider;
  if (terrainProvider) {
    try {
      const cartographic = Cartographic.fromDegrees(longitude, latitude);
      const [cartoWithHeight] = await sampleTerrainMostDetailed(
        terrainProvider,
        [cartographic],
      );
      height = cartoWithHeight.height ?? 0;
    } catch (err) {
      console.error('Error while getting the height: ', err);
    }
  }

  const destination = Cartesian3.fromDegrees(
    longitude,
    latitude,
    height + heightOffset,
  );
  viewer.camera.flyTo({ destination, ...flyToOptions });
}
